import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { caseStudiesData } from '../data/mockData';

const CaseStudyCard = ({ study, isActive, onClick }) => {
  return (
    <div 
      className={`cursor-pointer transition-all duration-300 ${
        isActive ? 'opacity-100' : 'opacity-50 hover:opacity-75'
      }`}
      onClick={onClick}
    >
      <div className="aspect-[16/10] overflow-hidden mb-4 border border-[#2a2a2a]">
        <img 
          src={study.image}
          alt={study.title}
          className="w-full h-full object-cover"
        />
      </div>
      <span className="body-small text-[#c9a962] uppercase tracking-wider">
        {study.category}
      </span>
      <h3 className="heading-3 text-white">{study.title}</h3>
    </div>
  );
};

const CaseStudies = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeStudy = caseStudiesData[activeIndex];

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? caseStudiesData.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === caseStudiesData.length - 1 ? 0 : prev + 1));
  };

  return (
    <section id="case-studies" className="section-padding bg-[#0a0a0a]">
      <div className="container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-4">
              Results
            </span>
            
            <h2 className="heading-1 text-white">
              Case Studies
            </h2>
          </div>
          
          {/* Navigation Arrows */}
          <div className="flex gap-2">
            <button 
              onClick={handlePrev}
              className="w-12 h-12 border border-[#c9a962] text-[#c9a962] flex items-center justify-center hover:bg-[#c9a962] hover:text-[#0a0a0a] transition-all duration-300"
              aria-label="Previous case study"
            >
              <ArrowLeft size={20} />
            </button>
            <button 
              onClick={handleNext}
              className="w-12 h-12 border border-[#c9a962] text-[#c9a962] flex items-center justify-center hover:bg-[#c9a962] hover:text-[#0a0a0a] transition-all duration-300"
              aria-label="Next case study"
            >
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Image & Thumbnails */}
          <div>
            <div className="aspect-[4/3] overflow-hidden mb-6 image-hover border border-[#2a2a2a]">
              <img 
                src={activeStudy.image}
                alt={activeStudy.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Thumbnails */}
            <div className="grid grid-cols-3 gap-4">
              {caseStudiesData.map((study, index) => (
                <CaseStudyCard 
                  key={study.id} 
                  study={study} 
                  isActive={index === activeIndex}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </div>
          </div>
          
          {/* Right - Details */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-xs font-medium uppercase tracking-wider text-[#0a0a0a] bg-[#c9a962] px-3 py-1">
                {activeStudy.category}
              </span>
              <span className="flex items-center gap-2 body-small text-[#808080]">
                <Clock size={14} />
                {activeStudy.duration}
              </span>
            </div>
            
            <h3 className="heading-1 text-white mb-2">
              {activeStudy.title}
            </h3>
            <p className="body-large text-[#808080] mb-8">
              {activeStudy.subtitle}
            </p>
            
            {/* Challenge & Solution */}
            <div className="space-y-6 mb-8">
              <div>
                <h4 className="heading-3 text-[#c9a962] mb-2">The Challenge</h4>
                <p className="body-regular text-[#b3b3b3]">{activeStudy.challenge}</p>
              </div>
              
              <div>
                <h4 className="heading-3 text-[#c9a962] mb-2">The Solution</h4>
                <p className="body-regular text-[#b3b3b3]">{activeStudy.solution}</p>
              </div>
            </div>
            
            {/* Results Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {activeStudy.results.map((result, index) => (
                <div key={index} className="bg-[#141414] border border-[#2a2a2a] p-4 text-center">
                  <span className="block text-2xl md:text-3xl font-medium text-[#c9a962]">
                    {result.metric}
                  </span>
                  <span className="body-small text-[#808080]">{result.label}</span>
                </div>
              ))}
            </div>
            
            {/* Testimonial */}
            <div className="border-l-2 border-[#c9a962] pl-6">
              <p className="body-large text-[#b3b3b3] italic">
                "{activeStudy.testimonial}"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
